import { BackofficeResourceSelect } from '@/backoffice/features/rbac/components/resource-selectors/backoffice-resource-select';
import { POLICIES } from '@/backoffice/features/rbac/policies/policies.constants';
import {
  useCreateSuperUserMutation,
  useCreateSuperUserRoleMutation,
  useCreateSuperUserRoleUserLinkMutation,
  useDeleteSuperUserMutation,
  useDeleteSuperUserRoleMutation,
  useDeleteSuperUserRoleUserLinkMutation,
  useReadAllSuperUserRolesQuery,
  useReadSuperUserRolesForSelectQuery,
  useReadAllSuperUsersQuery,
  useReadSuperUsersForSelectQuery,
  useReadSuperTenantQuery,
  useReadSuperUserQuery,
  useReadSuperUserRoleQuery,
  useReadSuperUserRoleUserLinksForUserQuery,
  useReadSuperUserRoleUserLinksForUserRoleQuery,
  useReadSuperUserRolesForOwnUserQuery,
  useReadSuperUsersForUserRoleQuery,
  useUpdateSuperUserMutation,
  useUpdateSuperUserRoleMutation,
  useUpdateSuperUserRoleUserLinkMutation,
} from '@/backoffice/redux/api/backoffice/backoffice-api';
import { ResourceTypeEnum } from '@/shared/components/ui/resource-icon/resource-icon.types';
import {
  TRbacApi,
  TRbacConfig,
  TRbacResourceSelectors,
  TRbacTenantSettings,
} from '@/shared/features/rbac/context/rbac-config-context.types';

const api = {
  useReadAllRolesQuery: useReadAllSuperUserRolesQuery,
  useReadRolesForSelectQuery: useReadSuperUserRolesForSelectQuery,
  useReadRoleQuery: useReadSuperUserRoleQuery,
  // Only console screens call the base read; the full super-user list already
  // carries every field it selects.
  useReadAllUsersBaseQuery: useReadAllSuperUsersQuery,
  useReadAllUsersQuery: useReadAllSuperUsersQuery,
  useReadUsersForSelectQuery: useReadSuperUsersForSelectQuery,
  useReadUserQuery: useReadSuperUserQuery,
  useReadUsersForRoleQuery: useReadSuperUsersForUserRoleQuery,
  useReadUserRoleLinksForUserQuery: useReadSuperUserRoleUserLinksForUserQuery,
  useReadUserRoleLinksForRoleQuery:
    useReadSuperUserRoleUserLinksForUserRoleQuery,
  useCreateUserMutation: useCreateSuperUserMutation,
  useUpdateUserMutation: useUpdateSuperUserMutation,
  useDeleteUserMutation: useDeleteSuperUserMutation,
  useCreateUserRoleMutation: useCreateSuperUserRoleMutation,
  useUpdateUserRoleMutation: useUpdateSuperUserRoleMutation,
  useDeleteRoleMutation: useDeleteSuperUserRoleMutation,
  useLinkRoleToUserMutation: useCreateSuperUserRoleUserLinkMutation,
  useRemoveRoleFromUserMutation: useDeleteSuperUserRoleUserLinkMutation,
  useUpdateUserRoleLinkMutation: useUpdateSuperUserRoleUserLinkMutation,
  useReadOwnUserRolesQuery: useReadSuperUserRolesForOwnUserQuery,
} satisfies Record<keyof TRbacApi, unknown>;

const useSuperTenantSettings = (): TRbacTenantSettings => {
  const { data, isLoading } = useReadSuperTenantQuery();

  return {
    ssoEnabled: false,
    manageSsoUsersInSda: false,
    companyName: data?.readSuperTenant?.companyName,
    isLoadingTenant: isLoading,
  };
};

const BACKOFFICE_RESOURCES = [
  ResourceTypeEnum.BackofficeUser,
  ResourceTypeEnum.BackofficeUserRole,
  ResourceTypeEnum.BackofficeTenant,
];

const resourceSelectors = Object.fromEntries(
  BACKOFFICE_RESOURCES.map((resourceType) => [
    resourceType,
    BackofficeResourceSelect,
  ]),
) as TRbacResourceSelectors;

export const backofficeRbacConfig: TRbacConfig = {
  api: api as unknown as TRbacApi,
  policies: POLICIES,
  permissionActions: {
    createUser: 'backofficeUser:createUser',
    readUser: 'backofficeUser:readUser',
    updateUser: 'backofficeUser:updateUser',
    deleteUser: 'backofficeUser:deleteUser',
    createUserRole: 'backofficeUserRole:createUserRole',
    readUserRole: 'backofficeUserRole:readUserRole',
    updateUserRole: 'backofficeUserRole:updateUserRole',
    deleteUserRole: 'backofficeUserRole:deleteUserRole',
    linkUserRole: 'backofficeUserRole:linkUserRole',
    unlinkUserRole: 'backofficeUserRole:unlinkUserRole',
  },
  // SuperUserFilterInput has no `name`; the searchable field is `fullNameLower`.
  userSearchField: 'fullNameLower',
  resourceSelectors,
  // Tags are never supported in Backoffice;
  resourceAggregations: { group: true, tags: false },
  useTenantSettings: useSuperTenantSettings,
  // Backoffice resource types are not feature-flagged.
  useIsResourceTypeEnabled: () => () => true,
  // deleteSuperUserRole has no force argument.
  supportsForceRoleDeletion: false,
  rbacPermissionChecks: {},
};
